'use client';

import { motion, useReducedMotion } from 'motion/react';

/**
 * Animated abstract visual for the frontpage hero: floating blurred orbs
 * (blue + green) and subtle grid. No image—modern, bold, clean.
 * Respects prefers-reduced-motion. Blur radii kept moderate for Safari performance.
 */
export function HeroHomeVisual() {
  const reducedMotion = useReducedMotion();

  const orbDuration = reducedMotion ? 0 : 14;
  const gridOpacity = reducedMotion ? 0.02 : 0.035;

  // When reduced motion: use static gradient instead of 5 blurred orbs (Safari perf)
  if (reducedMotion) {
    return (
      <div
        className="absolute inset-0 z-0 overflow-hidden pointer-events-none"
        aria-hidden
      >
        <div
          className="absolute inset-0 opacity-60"
          style={{
            background: 'radial-gradient(ellipse 60% 50% at 20% 30%, rgba(0, 180, 126, 0.15), transparent), radial-gradient(ellipse 50% 60% at 80% 70%, rgba(0, 119, 182, 0.12), transparent)',
          }}
        />
        <div className="absolute inset-0 text-white" style={{ opacity: gridOpacity }}>
          <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="hero-grid-reduced" width="48" height="48" patternUnits="userSpaceOnUse">
                <path d="M 48 0 L 0 0 0 48" fill="none" stroke="currentColor" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hero-grid-reduced)" />
          </svg>
        </div>
      </div>
    );
  }

  return (
    <div
      className="absolute inset-0 z-0 overflow-hidden pointer-events-none"
      aria-hidden
    >
      {/* Blur radii kept moderate (50–60px) for Safari performance; large blur is expensive */}
      <motion.div
        className="absolute top-[10%] left-[15%] w-[min(80vw,420px)] h-[min(80vw,420px)] rounded-full bg-brand/25 blur-[50px] will-change-transform"
        animate={{
          x: [0, 90, -50, 0],
          y: [0, -60, 70, 0],
          scale: [1, 1.2, 0.88, 1],
        }}
        transition={{
          duration: orbDuration,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
      <motion.div
        className="absolute bottom-[15%] right-[10%] w-[min(70vw,380px)] h-[min(70vw,380px)] rounded-full bg-highlight/22 blur-[55px] will-change-transform"
        animate={{
          x: [0, -140, 100, 0],
          y: [0, 100, -90, 0],
          scale: [1, 0.85, 1.22, 1],
        }}
        transition={{
          duration: orbDuration,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
      <motion.div
        className="absolute top-[50%] left-[50%] w-[min(50vw,280px)] h-[min(50vw,280px)] -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand/15 blur-[45px] will-change-transform"
        animate={{
          x: ['-50%', 'calc(-50% + 60px)', 'calc(-50% - 45px)', '-50%'],
          y: ['-50%', 'calc(-50% - 55px)', 'calc(-50% + 50px)', '-50%'],
          scale: [1, 1.15, 0.9, 1],
        }}
        transition={{
          duration: orbDuration - 1,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
      <motion.div
        className="absolute top-[25%] right-[25%] w-[min(40vw,220px)] h-[min(40vw,220px)] rounded-full bg-highlight/20 blur-[50px] will-change-transform"
        animate={{
          x: [0, 85, -70, 0],
          y: [0, -75, 65, 0],
          scale: [1, 1.18, 0.88, 1],
        }}
        transition={{
          duration: orbDuration + 1,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
      <motion.div
        className="absolute bottom-[30%] left-[20%] w-[min(35vw,200px)] h-[min(35vw,200px)] rounded-full bg-white/15 blur-[40px] will-change-transform"
        animate={{
          x: [0, -50, 55, 0],
          y: [0, 45, -35, 0],
          scale: [1, 0.9, 1.1, 1],
        }}
        transition={{
          duration: orbDuration - 2,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Subtle grid */}
      <div
        className="absolute inset-0 text-white"
        style={{ opacity: gridOpacity }}
      >
        <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern
              id="hero-grid"
              width="48"
              height="48"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 48 0 L 0 0 0 48"
                fill="none"
                stroke="currentColor"
                strokeWidth="0.5"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#hero-grid)" />
        </svg>
      </div>
    </div>
  );
}
